import type { GeomechanicsModuleKey } from '../../services/api';

export type WorkbenchModule = {
  key: GeomechanicsModuleKey;
  name: string;
  shortName: string;
  description: string;
  steps: string[];
  plannedStage: string;
  formula?: string;
};

/**
 * B-compatible user navigation. "Ready" flags are intentionally omitted:
 * this shell must not advertise calculation capabilities before later stages.
 */
export const WORKBENCH_MODULES: readonly WorkbenchModule[] = [
  {
    key: '00',
    name: '数据总览',
    shortName: '总览',
    description: '集中查看受控数据集、工作台运行和各模块确认后的成果。',
    steps: ['数据汇总全览'],
    plannedStage: '阶段 3',
  },
  {
    key: '01',
    name: '数据导入与预处理',
    shortName: '导入',
    description: '复用 DataHub 的原文件、字段单位、质控、审核和版本链。',
    steps: ['文件导入与解析', '曲线道选择', '标准列映射', '质量控制与补全', '统一深度表与输出'],
    plannedStage: '阶段 4',
  },
  {
    key: '02',
    name: '上覆岩层压力',
    shortName: '上覆',
    description: '密度趋势拟合、浅层补偿和垂向应力积分。',
    steps: ['输入核对与参数', '计算输出与预览'],
    plannedStage: '阶段 5',
    formula: 'Sv(z) = g ∫₀ᶻ ρ(ζ) dζ',
  },
  {
    key: '03',
    name: '地层压力',
    shortName: '孔压',
    description: '保留 DT、密度、电阻率和 DC 四种 Eaton 路径及独立候选结果。',
    steps: ['数据确认', '计算', '数据汇总全览', '数据拼接/调整/提交'],
    plannedStage: '阶段 5',
    formula: 'Pp = Sv − (Sv − Pn)(Xobs / Xn)ⁿ',
  },
  {
    key: '04',
    name: '岩石力学参数',
    shortName: '岩参',
    description: '泥质含量、地层划分、分层公式试算和实验标定。',
    steps: ['泥质含量计算', '地层划分', '分层公式匹配与试算', '实验标定'],
    plannedStage: '阶段 6',
    formula: 'E = 2G(1 + ν)',
  },
  {
    key: '05',
    name: '地应力大小',
    shortName: '地应力',
    description: '计算最小/最大水平主应力并保留井眼坐标系张量。',
    steps: ['数据确认', '分层模型与参数', '实测约束标定', '结果确认/提交'],
    plannedStage: '阶段 6',
    formula: 'Sh = ν / (1 − ν) · (Sv − αPp) + αPp',
  },
  {
    key: '06',
    name: '破裂压力',
    shortName: '破裂',
    description: '多模型候选、LOT/XLOT/FIT/漏失事件校核与控制值。',
    steps: ['数据确认', '破裂模型与分段', 'LOT/FIT/漏失校核', '结果确认/提交'],
    plannedStage: '阶段 7',
    formula: 'Pf = 3Sh − SH − αPp + T₀',
  },
  {
    key: '07',
    name: '坍塌压力',
    shortName: '坍塌',
    description: '五种强度准则、井周方位搜索与井壁事件标定。',
    steps: ['数据确认', '强度准则与分段', '井壁事件校核', '结果确认/提交'],
    plannedStage: '阶段 7',
    formula: "σ₁′ = UCS + qσ₃′",
  },
  {
    key: '08',
    name: '综合评价与成果输出',
    shortName: '综合',
    description: '合成上下限、现场约束、控制码、缺失码与 1–4 级风险。',
    steps: ['数据确认', '窗口约束与阈值', '风险分段复核', '成果版本与提交'],
    plannedStage: '阶段 7',
  },
  {
    key: '09',
    name: '区块地质力学数据',
    shortName: '区块',
    description: '多井分层/分区建模、交叉验证、覆盖度和低置信区评价。',
    steps: ['审核井管理', '属性与层位样本', '网格建模与预览', '交叉验证与提交'],
    plannedStage: '阶段 8',
  },
  {
    key: '10',
    name: '后处理与井筒综合可视化',
    shortName: '后处理',
    description: '汇总已确认成果，检查深度基准并为曲线后处理与井筒综合可视化提供入口。',
    steps: [],
    plannedStage: '阶段 8',
  },
] as const;

export const WORKBENCH_MODULE_KEYS = WORKBENCH_MODULES.map((module) => module.key);

export function isModuleKey(value: unknown): value is GeomechanicsModuleKey {
  return typeof value === 'string'
    && WORKBENCH_MODULE_KEYS.includes(value as GeomechanicsModuleKey);
}

export function getWorkbenchModule(key: GeomechanicsModuleKey): WorkbenchModule {
  return WORKBENCH_MODULES.find((module) => module.key === key) ?? WORKBENCH_MODULES[0];
}
